import React, { useEffect, useMemo, useState } from 'react';
// Même image vide que les box image de l'éditeur.
import placeholderImage from '../../../images/placeholder-img.png';
import styles from './TableConfigBody.module.css';
import {
  canMergeSelection, canUnmergeSelection, computeMergedCellLayout, isMultiSelectClick, mergedCellSpanKey, rectToKeys,
} from '../tableStructure';
import { convertPadding, getCellStyle, getHeaderStyle } from '../tableCellStyle';
import { cellStyleEntry, rowStyleCss } from '../tableRowStyle';

/**
 * Aperçu de « Configurer le tableau » pour un tableau custom (non variable) :
 * ses vraies lignes, ses fusions et le style de ses lignes, là où un tableau
 * variable n'a qu'une forme d'exemple (cf. TableConfigBody).
 *
 * Mêmes gestes que dans le document : un clic choisit une case (le panel de
 * droite règle alors le style de sa ligne ou de la case), Maj + clic étend la
 * sélection en rectangle, puis « Fusionner ».
 *
 * `columns` : `[{ key, name, width (pourcent), align, headerAlign, type }]`,
 * dans l'ordre du tableau.
 * `rows` : `[{ key, cells: { [clé de colonne]: texte }, align: { [clé]: alignement de la case } }]`.
 * `rowStyles` : style de chaque ligne par clé (Map, cf. customRowStyles).
 * `onSelect({ rowKey, columnKey } | null)` : case choisie.
 * `onMerge(keys)` / `onUnmerge(keys)` : `keys` = `{ rowKeys, columnKeys }` (cf. rectToKeys).
 */

const TEXT_ALIGN = { center: 'center', end: 'right', justify: 'justify' };
const toTextAlign = (align) => TEXT_ALIGN[align] ?? 'left';

export default function CustomTablePreview({
  t, widgets, columns, rows, mergedCells, rowStyles = null, previewStyle = null, onSelect, onMerge, onUnmerge,
}) {
  // { anchor: { r, c }, r0, r1, c0, c1 } : case cliquée et rectangle sélectionné.
  const [selection, setSelection] = useState(null);
  const layout = useMemo(() => computeMergedCellLayout(rows, columns, mergedCells), [rows, columns, mergedCells]);

  // Ligne ou colonne ajoutée, retirée ou déplacée : les indices ne désignent
  // plus les mêmes cases.
  const shape = `${rows.map((row) => row.key).join('|')}#${columns.map((col) => col.key).join('|')}`;
  useEffect(() => setSelection(null), [shape]);

  const anchor = selection?.anchor;
  const selectedRowKey = anchor ? rows[anchor.r]?.key ?? null : null;
  const selectedColumnKey = anchor ? columns[anchor.c]?.key ?? null : null;
  useEffect(() => {
    onSelect?.(selectedRowKey ? { rowKey: selectedRowKey, columnKey: selectedColumnKey } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKey, selectedColumnKey]);

  const selectCell = (r, c, event) => {
    if (isMultiSelectClick(event) && anchor) {
      setSelection({
        anchor,
        r0: Math.min(anchor.r, r),
        r1: Math.max(anchor.r, r),
        c0: Math.min(anchor.c, c),
        c1: Math.max(anchor.c, c),
      });
      return;
    }
    setSelection({ anchor: { r, c }, r0: r, r1: r, c0: c, c1: c });
  };

  const keys = rectToKeys(rows, columns, selection);
  const mergeable = canMergeSelection(keys, mergedCells);
  const unmergeable = canUnmergeSelection(keys, mergedCells);
  // Après la fusion, la case haut-gauche reste choisie : son style se règle
  // sans recliquer.
  const keepTopLeft = () => selection && setSelection({
    anchor: { r: selection.r0, c: selection.c0 },
    r0: selection.r0,
    r1: selection.r0,
    c0: selection.c0,
    c1: selection.c0,
  });

  const isSelected = (r, c, rowSpan, colSpan) => !!selection &&
    r <= selection.r1 && r + rowSpan - 1 >= selection.r0 &&
    c <= selection.c1 && c + colSpan - 1 >= selection.c0;

  // Style du document, comme l'aperçu d'un tableau variable : cases rendues par
  // les fonctions de Cell / Header, puis le style de leur ligne et de la case.
  const lastIdx = columns.length - 1;
  const spacing = previewStyle && convertPadding(
    previewStyle.tableStyle.size,
    previewStyle.tableStyle.customCellPadding,
    previewStyle.tableStyle.customCellPaddingUnit,
  );
  const ruleAt = (rowIdx) => rowStyles?.get(rows[rowIdx]?.key) ?? null;
  const headerStyle = (col, colIdx) => ({
    ...(previewStyle && getHeaderStyle({ ...previewStyle, isLastColumn: colIdx === lastIdx })),
    textAlign: toTextAlign(col.headerAlign ?? col.align),
    ...rowStyleCss(null, ruleAt(0)),
    width: `${col.width}%`,
  });
  const cellStyle = (rowIdx, col, colIdx, span) => {
    const rule = ruleAt(rowIdx);
    const entry = cellStyleEntry(rule, col.key);
    const isLastColumn = colIdx + span.colSpan - 1 === lastIdx;
    return {
      ...(previewStyle && getCellStyle({ ...previewStyle, isEven: rowIdx % 2 === 0, isLastColumn, width: 'auto' })),
      textAlign: toTextAlign(rows[rowIdx].align?.[col.key] ?? entry?.align ?? rule?.align ?? col.align),
      ...rowStyleCss(rule, ruleAt(rowIdx + span.rowSpan), { isFirstColumn: colIdx === 0, isLastColumn }),
      ...rowStyleCss(entry),
    };
  };
  const cellContent = (text) => (previewStyle ? (
    <div
      className={styles.previewCellContent}
      style={{ padding: spacing.padding, minHeight: spacing.minHeight, whiteSpace: 'pre-line' }}
    >
      {text || ' '}
    </div>
  ) : text);

  return (
    <>
      <div className={`${styles.note} ${styles.previewHint}`}>{t('table-custom-preview-hint')}</div>
      <div className={styles.previewScroll}>
        <table className={styles.previewTable} data-styled={!!previewStyle || undefined}>
          <thead>
            <tr>
              {columns.map((col, colIdx) => (
                <th key={col.key} style={headerStyle(col, colIdx)}>{cellContent(col.name)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={row.key} data-clickable>
                {columns.map((col, colIdx) => {
                  const cellKey = mergedCellSpanKey(row.key, col.key);
                  if (layout.coveredCells.has(cellKey)) return null;
                  const span = layout.spanByCell.get(cellKey) ?? { rowSpan: 1, colSpan: 1 };
                  const text = row.cells[col.key];
                  return (
                    <td
                      key={col.key}
                      rowSpan={span.rowSpan > 1 ? span.rowSpan : undefined}
                      colSpan={span.colSpan > 1 ? span.colSpan : undefined}
                      style={cellStyle(rowIdx, col, colIdx, span)}
                      data-selected={isSelected(rowIdx, colIdx, span.rowSpan, span.colSpan) || undefined}
                      onClick={(event) => selectCell(rowIdx, colIdx, event)}
                    >
                      {col.type === 'image' && text
                        ? <img className={styles.previewImage} src={placeholderImage} alt="" aria-hidden="true" />
                        : cellContent(text)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.previewActions}>
        <widgets.TextButton
          name="merge"
          disabled={!mergeable}
          onClick={() => {
            onMerge(keys);
            keepTopLeft();
          }}
        >
          {t('table-merge-cells')}
        </widgets.TextButton>
        <widgets.TextButton
          name="unmerge"
          disabled={!unmergeable}
          onClick={() => {
            onUnmerge(keys);
            keepTopLeft();
          }}
        >
          {t('table-unmerge-cells')}
        </widgets.TextButton>
      </div>
    </>
  );
}
